import React, { useEffect, useMemo, useRef, useState } from "react";
import { Search, MapPin, X } from "lucide-react";
import { searchPlaces, placeCount, flagEmoji } from "@/lib/globeSearch";

/** Search any country or major city and fly the live globe to it. Dispatches `mini-globe-focus`. */
export default function GlobeSearch({ onPlace, globeTestId = "globe-main", className = "" }) {
  const [q, setQ] = useState("");
  const [open, setOpen] = useState(false);
  const [active, setActive] = useState(0);
  const [picked, setPicked] = useState(null);
  const box = useRef(null);
  const results = useMemo(() => searchPlaces(q, 8), [q]);
  const counts = useMemo(() => placeCount(), []);
  useEffect(() => {
    const onDoc = (e) => { if (box.current && !box.current.contains(e.target)) setOpen(false); };
    document.addEventListener("pointerdown", onDoc);
    return () => document.removeEventListener("pointerdown", onDoc);
  }, []);
  const choose = (p) => {
    setPicked(p); setQ(""); setOpen(false); setActive(0);
    window.dispatchEvent(new CustomEvent("mini-globe-focus", { detail: { lat: p.lat, lng: p.lng, code: p.code, label: p.kind === "city" ? p.name : null, altitude: p.altitude, testId: globeTestId } }));
    onPlace?.(p);
  };
  const onKey = (e) => {
    if (!results.length) return;
    if (e.key === "ArrowDown") { e.preventDefault(); setOpen(true); setActive((i) => (i + 1) % results.length); }
    else if (e.key === "ArrowUp") { e.preventDefault(); setActive((i) => (i - 1 + results.length) % results.length); }
    else if (e.key === "Enter") { e.preventDefault(); choose(results[active] || results[0]); }
    else if (e.key === "Escape") setOpen(false);
  };
  return (
    <div ref={box} className={"relative " + className} data-testid="globe-search">
      <label htmlFor="globe-search-input" className="sr-only">Search a country or city</label>
      <div className="flex items-center gap-2 rounded-full border-[3px] border-slate-900 bg-white pl-3 pr-2 shadow-[0_4px_0_0_rgba(15,23,42,1)] focus-within:ring-4 focus-within:ring-sky-200">
        <Search size={18} strokeWidth={3} className="shrink-0 text-slate-700" aria-hidden />
        <input id="globe-search-input" value={q} onChange={(e) => { setQ(e.target.value); setOpen(true); setActive(0); }} onFocus={() => setOpen(true)} onKeyDown={onKey}
          placeholder={`Find a country or city… (${counts.countries} countries, ${counts.cities} cities)`} autoComplete="off" spellCheck={false}
          role="combobox" aria-expanded={open && results.length > 0} aria-controls="globe-search-results" aria-autocomplete="list"
          className="min-h-[48px] w-full bg-transparent text-base font-bold outline-none placeholder:font-semibold placeholder:text-slate-400" data-testid="globe-search-input" />
        {q && <button type="button" onClick={() => { setQ(""); setOpen(false); }} aria-label="Clear search" className="grid h-9 w-9 place-items-center rounded-full hover:bg-slate-100" data-testid="globe-search-clear"><X size={16} strokeWidth={3} /></button>}
      </div>
      {open && q.trim().length >= 2 && (
        <ul id="globe-search-results" role="listbox" className="absolute left-0 right-0 z-30 mt-2 max-h-80 overflow-auto rounded-2xl border-[3px] border-slate-900 bg-white p-1 shadow-[0_6px_0_0_rgba(15,23,42,1)]" data-testid="globe-search-results">
          {results.length === 0 && <li className="px-3 py-3 text-sm font-bold text-slate-600" data-testid="globe-search-empty">No place called "{q}" yet — try a country, a capital or a big city.</li>}
          {results.map((p, i) => (
            <li key={p.id} role="option" aria-selected={i === active}>
              <button type="button" onMouseEnter={() => setActive(i)} onClick={() => choose(p)} data-testid={`globe-search-result-${p.id}`}
                className={"flex w-full items-center gap-3 rounded-xl px-3 py-2.5 text-left " + (i === active ? "bg-yellow-200" : "hover:bg-slate-50")}>
                <span className="text-2xl" aria-hidden>{p.kind === "country" ? flagEmoji(p.code) : "📍"}</span>
                <span className="min-w-0 flex-1">
                  <span className="block truncate font-heading font-bold">{p.name}</span>
                  <span className="block truncate text-xs text-slate-600">{p.sub}</span>
                </span>
                <span className="rounded-full border-2 border-slate-900 px-2 py-0.5 text-[10px] font-black uppercase">{p.kind === "country" ? "Country" : p.capital ? "Capital" : "City"}</span>
              </button>
            </li>
          ))}
        </ul>
      )}
      {picked && !open && (
        <p className="mt-2 inline-flex items-center gap-1 rounded-full bg-slate-900 px-3 py-1 text-xs font-bold text-white" role="status" data-testid="globe-search-picked">
          <MapPin size={12} strokeWidth={3} /> Showing {picked.name}{picked.kind === "city" && picked.country ? `, ${picked.country.name}` : ""}
        </p>
      )}
    </div>
  );
}
